import type { SpeedToLeadStaffEntry, StaffRow } from '../../types/speedToLead.js';

// The ordered staff call list as the two screens show it. PURE.
//
// ⚠️ NAME AND ROLE ARE READ FROM THE CLUB'S TEAM (BusinessProfile.team) EVERY TIME; the saved list holds only
// { teamMemberId, receivesLeadCalls } and its ORDER. A person who left the team stays in the list as a
// `removed` row with a visible warning (skipped at call time) until someone removes them.

export interface TeamMemberLite {
  id: string;
  name: string;
  role: string;
  hasMobile: boolean;
}

/** Saved order first (removed people included), then the team members not yet in the list, switched off. */
export function buildStaffRows(team: readonly TeamMemberLite[], staffList: readonly SpeedToLeadStaffEntry[]): StaffRow[] {
  const byId = new Map(team.map((m) => [m.id, m]));
  const rows: StaffRow[] = staffList.map((s) => {
    const m = byId.get(s.teamMemberId);
    return {
      teamMemberId: s.teamMemberId,
      name: m?.name ?? 'Former team member',
      role: m?.role ?? '',
      hasMobile: m?.hasMobile ?? false,
      receivesLeadCalls: s.receivesLeadCalls,
      listed: true,
      removed: !m,
    };
  });
  const listed = new Set(staffList.map((s) => s.teamMemberId));
  for (const m of team) {
    if (listed.has(m.id)) continue;
    rows.push({ teamMemberId: m.id, name: m.name, role: m.role, hasMobile: m.hasMobile, receivesLeadCalls: false, listed: false, removed: false });
  }
  return rows;
}

export function moveRow(rows: readonly StaffRow[], index: number, delta: -1 | 1): StaffRow[] {
  const to = index + delta;
  if (index < 0 || index >= rows.length || to < 0 || to >= rows.length) return [...rows];
  const next = [...rows];
  [next[index], next[to]] = [next[to]!, next[index]!];
  return next;
}

export function setRowOn(rows: readonly StaffRow[], index: number, on: boolean): StaffRow[] {
  return rows.map((r, i) => (i === index ? { ...r, receivesLeadCalls: on, listed: r.listed || on } : r));
}

export function removeRow(rows: readonly StaffRow[], index: number): StaffRow[] {
  return rows.filter((_, i) => i !== index);
}

/** What is saved: every row that is in the list (or was switched on), in order. Unlisted, switched-off people are not saved. */
export function toStaffList(rows: readonly StaffRow[]): SpeedToLeadStaffEntry[] {
  return rows.filter((r) => r.listed).map((r) => ({ teamMemberId: r.teamMemberId, receivesLeadCalls: r.receivesLeadCalls }));
}

/** A person switched on who has no mobile number cannot be called: surfaced so it is fixed, not discovered. */
export function rowsNeedingMobile(rows: readonly StaffRow[]): StaffRow[] {
  return rows.filter((r) => r.receivesLeadCalls && !r.hasMobile && !r.removed);
}
